import React, { FC, useEffect } from 'react';
import parse from 'date-fns/parse';
import { useFormik } from 'formik';
import { Theme, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';

import { TwButton, TwButtonType } from '../TwButtons/TwButtons';
import TwDatePicker from '../TwDatePicker/TwDatePicker';
import TwDialog from '../TwDialog/TwDialog';
import TwTimePicker from '../TwTimePicker/TwTimePicker';
import DateUtils from '../../utils/dateUtils';
import { logRocketTrack } from '../../utils/logRocketUtils';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import useAuth from '../../providers/Auth/Auth.provider';

type EditDateConfig = {
  use24HourClock: boolean;
  shortOffset: boolean;
};

interface TwEditDateTimeProps {
  prevStopOutTime?: Date;
  prevDateTime?: Date;
  datetime: Date;
  nextDateTime?: Date;
  nextStopInTime?: Date;
  open: boolean;
  title?: string;
  dateInputLabel?: string;
  config?: EditDateConfig;
  onClose?: () => void;
  onSuccess?: (ISODate: string) => void;
}

const TwEditDateTime: FC<TwEditDateTimeProps> = ({
  prevStopOutTime,
  prevDateTime,
  datetime,
  nextDateTime,
  nextStopInTime,
  config,
  dateInputLabel,
  open,
  title,
  onClose,
  onSuccess
}) => {
  const { addNotification } = useNotifications();
  const {
    data: { userData }
  } = useAuth();
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  useEffect(() => {
    setFieldValue('date', datetime);
    setFieldValue('time', datetime);
  }, [datetime]);

  const handleClose = (): void => {
    onClose && onClose();
  };

  const { values, errors, handleSubmit, setFieldValue } = useFormik({
    initialValues: {
      date: new Date(),
      time: new Date()
    },
    onSubmit: (values): void => {
      const { date, time } = values;

      if (!date || !time) {
        addNotification({
          message: 'Please select date and time',
          variant: 'error'
        });
      } else {
        const { valid, message } = isSelectedDatetimeValid(DateUtils.getISODate(date, time));

        valid && onSuccess
          ? onSuccess(DateUtils.getISODate(date, time))
          : addNotification({
              message,
              variant: 'error'
            });
      }
    }
  });

  /**
   * Checks if selected date time is valid regarding current, previous and next stop times
   * @param datetime
   * @returns Object with 'valid' (boolean) and message (string) properties
   */
  const isSelectedDatetimeValid = (datetime: string): { valid: boolean; message: string } => {
    const prevDate = prevDateTime && DateUtils.getISODate(prevDateTime, prevDateTime);
    const nextDate = nextDateTime && DateUtils.getISODate(nextDateTime, nextDateTime);
    const prevStopDate = prevStopOutTime && DateUtils.getISODate(prevStopOutTime, prevStopOutTime);
    const nextStopDate = nextStopInTime && DateUtils.getISODate(nextStopInTime, nextStopInTime);

    const currentTime = new Date(datetime);
    const prevTime = prevDate && new Date(prevDate);
    const nextTime = nextDate && new Date(nextDate);
    const prevStopTime = prevStopDate && new Date(prevStopDate);
    const nextStopTime = nextStopDate && new Date(nextStopDate);

    // Check if selected date time is in the future
    if (currentTime && currentTime.setSeconds(0, 0) > new Date().setSeconds(0, 0)) {
      return {
        valid: false,
        message: `Please adjust date and time, it cannot be in the future`
      };
    }

    // Validate if selected IN date time is before current OUT time
    if (
      (currentTime && prevTime && currentTime.setSeconds(0, 0) < prevTime.setSeconds(0, 0)) ||
      (currentTime && nextTime && currentTime.setSeconds(0, 0) > nextTime.setSeconds(0, 0))
    ) {
      logRocketTrack('Load update from driver', {
        action: 'Stop check in and out times validation failed (in time is after out time)',
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      return {
        valid: false,
        message: `Please adjust date and time, stop 'actual out' time cannot be before than stop 'actual in' time`
      };
    }

    // Validate if selected IN date time is after previous stop OUT time
    if (
      currentTime &&
      prevStopTime &&
      currentTime.setSeconds(0, 0) < prevStopTime.setSeconds(0, 0)
    ) {
      logRocketTrack('Load update from driver', {
        action: `Stop check in time validation failed, stop check in time must be after the previous stop's check out time.`,
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      return {
        valid: false,
        message: `Please adjust date and time, 'actual in' time cannot be before than 'actual out' time of the previous stop`
      };
    }

    // Validate if selected OUT date time is before next stop IN time
    if (
      currentTime &&
      nextStopTime &&
      currentTime.setSeconds(0, 0) > nextStopTime.setSeconds(0, 0)
    ) {
      logRocketTrack('Load update from driver', {
        action: `Stop check out time validation failed, stop check out time must be before the next stop's check in time.`,
        severity: 'low',
        tenant_id: userData?.tenant?.id,
        company_name: userData?.tenant?.company_name,
        email: userData?.email_address,
        user_first_name: userData?.first_name,
        user_last_name: userData?.last_name
      });
      return {
        valid: false,
        message: `Please adjust date and time, 'actual out' time cannot be later than 'actual in' time of the next stop`
      };
    }

    return {
      valid: true,
      message: ''
    };
  };

  return (
    <TwDialog
      open={open}
      title={title || 'Edit date time'}
      draggable
      fullScreen={matchesMobileOrTablet}
      onClickClose={(): void => {
        handleClose();
      }}
      actions={[
        <TwButton
          key="1"
          fullwidth={false}
          type={TwButtonType.secondary}
          label="Cancel"
          action={(): void => {
            handleClose();
          }}
        />,
        <TwButton
          key="2"
          fullwidth={false}
          type={TwButtonType.primary}
          label="Save"
          action={handleSubmit}
        />
      ]}
    >
      <Box
        component="form"
        display="flex"
        flexDirection={matchesMobileOrTablet ? 'column' : 'row'}
        gap={2}
      >
        <TwDatePicker
          name="date"
          label={dateInputLabel || 'Date'}
          value={values.date}
          placeholder="MM/DD/YY"
          onChange={(newDate): void => {
            const date = newDate as Date | null;
            setFieldValue('date', date);
          }}
          onInputBlur={(e): void => {
            try {
              const date = new Date(e.target.value);
              setFieldValue('date', date.toISOString() ? date : null);
            } catch (e) {
              setFieldValue('date', null);
            }
          }}
          error={!!errors['date']}
          containerSx={{ maxWidth: matchesMobileOrTablet ? '180px' : '172px' }}
        />
        <TwTimePicker
          name="time"
          label={`Actual time${config?.shortOffset ? ` (${config.shortOffset})` : ''}`}
          value={values.time}
          onChange={(newDate): void => {
            const date = newDate as Date | null;
            setFieldValue('time', date);
          }}
          onInputBlur={(e): void => {
            try {
              const date = parse(
                e.target.value as string,
                !config?.use24HourClock ? 'hh:mm aa' : 'HH:mm',
                new Date()
              );
              setFieldValue('time', date);
            } catch (e) {
              setFieldValue('time', null);
            }
          }}
          ampm={!config?.use24HourClock}
          placeholder={!config?.use24HourClock ? 'HH:MM AM' : 'HH:MM'}
          error={!!errors['time']}
          containerSx={{ maxWidth: matchesMobileOrTablet ? '180px' : '172px' }}
        />
      </Box>
    </TwDialog>
  );
};

export default TwEditDateTime;
